import { create } from 'zustand';

/**
 * Resolve the pathname a navigation is heading to.
 *
 * Next hands `onRouterTransitionStart` the raw `href` for push/replace and an absolute URL for
 * traverse (back/forward), so both shapes have to be handled. Returns null for anything
 * unparseable or off-site — those are full page loads, where the browser draws its own progress.
 *
 * This app has no `basePath`, so `new URL(href).pathname` and `usePathname()` agree. If a basePath
 * is ever added, strip it here before comparing.
 */
const resolveTargetPath = url => {
	if (typeof url !== 'string' || url === '') return null;
	try {
		// Reading location inside the try doubles as the guard for a non-browser context:
		// no location means no navigation to track, and the throw lands in the catch below.
		const { href, origin } = globalThis.location;
		const target = new URL(url, href);
		if (target.origin !== origin) return null;
		return target.pathname;
	} catch {
		return null;
	}
};

/**
 * Drives the global top loader. The producer is `src/instrumentation-client.js` — a non-React
 * module — so the flag has to live outside React; the consumer is `GlobalTopLoader` in the root
 * layout. Only the flag is global: the animated percentage stays local to the component so a
 * 250ms tick can't re-render every subscriber.
 */
export const useNavigationProgressStore = create((set, get) => ({
	// True between an accepted navigation start and the pathname commit that ends it.
	isNavigating: false,
	// Last pathname that actually committed. Seeded and kept current by useNavigationProgress.
	committedPath: null,

	/**
	 * Called from `onRouterTransitionStart` for every push / replace / traverse.
	 *
	 * Same-route navigations never change the pathname, so they would leave the bar with no end
	 * signal — skip them. That covers re-clicking the active sidebar link and, more importantly,
	 * the `router.replace(url, { scroll: false })` query-string sync that fires on every search
	 * keystroke, sort and page change (see useUrlState).
	 */
	start: url => {
		const target = resolveTargetPath(url);
		if (target === null || target === get().committedPath) return;
		set({ isNavigating: true });
	},

	/**
	 * The commit landed. Called with every committed pathname.
	 *
	 * Comparing against the stored `committedPath` rather than `location.pathname` is what makes
	 * back/forward work: `dispatchTraverseAction` runs inside `popstate`, when `location.pathname`
	 * is already the destination.
	 */
	settle: pathname => {
		const { committedPath, isNavigating } = get();
		if (!isNavigating && committedPath === pathname) return;
		set({ isNavigating: false, committedPath: pathname });
	},

	/** Safety valve: a navigation was cancelled or blocked and will never commit. */
	abort: () => {
		if (!get().isNavigating) return;
		set({ isNavigating: false });
	},

	/** Test/teardown helper — restores the initial state. */
	reset: () => set({ isNavigating: false, committedPath: null })
}));
